<template>
  <div>
    <!-- Warning modal -->
    <v2-modal
      :show="show && !showOrderSelection"
      title="Collection Update Disclaimer!"
      size="md"
      @close="closeModal"
    >
      <p class="v2-currency-update__message">
        Some SKUs with updated prices exist in orders from retailers. Would
        you like to update prices in not confirmed orders?
      </p>
      <template #footer>
        <v2-button
          variant="outline"
          :disabled="isSaving"
          @click="closeModal"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isSaving"
          :loading="isSaving"
          @click="handleDoNotUpdateOrders"
        >
          {{ isSaving ? 'Importing...' : 'Do not update orders' }}
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isSaving"
          @click="showOrderSelection = true"
        >
          Update orders
        </v2-button>
      </template>
    </v2-modal>

    <!-- Order selection modal -->
    <v2-modal
      :show="show && showOrderSelection"
      title="Updating Orders"
      size="lg"
      @close="closeOrderSelection"
    >
      <div class="v2-currency-update__search-row">
        <v2-input
          :value="searchText"
          placeholder="Search order id, retailer name..."
          class="v2-currency-update__search-input"
          @input="onSearchOrders"
        />
      </div>
      <div class="v2-currency-update__orders-header">
        <div class="v2-currency-update__orders-col v2-currency-update__orders-col--id">
          Order Id
        </div>
        <div class="v2-currency-update__orders-col v2-currency-update__orders-col--retailer">
          Retailer Name
        </div>
      </div>
      <div class="v2-currency-update__orders-list">
        <label
          v-for="order in orderList"
          :key="order.orderId"
          class="v2-currency-update__order-row"
        >
          <input
            type="checkbox"
            :value="order.orderId"
            :checked="selectedOrders.includes(order.orderId)"
            class="v2-currency-update__order-checkbox"
            @change="toggleOrder(order.orderId)"
          >
          <div class="v2-currency-update__orders-col v2-currency-update__orders-col--id">
            {{ order.orderSeqId }}
          </div>
          <div class="v2-currency-update__orders-col v2-currency-update__orders-col--retailer">
            {{ order.retailerName }}
          </div>
        </label>
      </div>
      <template #footer>
        <v2-button
          variant="outline"
          :disabled="isSaving"
          @click="closeOrderSelection"
        >
          Cancel
        </v2-button>
        <v2-button
          variant="primary"
          :disabled="isSaving"
          :loading="isSaving"
          @click="handleUpdateOrders"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Button from '@/components/v2/V2Button.vue'
import V2Input from '@/components/v2/V2Input.vue'

export default {
  name: 'V2CurrencyUpdateModals',
  components: {
    V2Modal,
    V2Button,
    V2Input,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    collectionId: {
      type: String,
      default: '',
    },
    orderData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      showOrderSelection: false,
      orderList: [],
      selectedOrders: [],
      searchText: '',
    }
  },
  computed: {
    isSaving() {
      return this.$store.state.collection.isUploadingCurrencyBook
    },
  },
  watch: {
    orderData(val) {
      this.orderList = val
    },
    show(val) {
      if (val) {
        this.orderList = this.orderData
        this.selectedOrders = []
        this.showOrderSelection = false
        this.searchText = ''
      }
    },
  },
  methods: {
    closeModal() {
      if (this.isSaving) return
      this.$emit('close')
      this.showOrderSelection = false
      this.selectedOrders = []
      this.searchText = ''
    },
    closeOrderSelection() {
      if (this.isSaving) return
      this.showOrderSelection = false
    },
    handleDoNotUpdateOrders() {
      this.$emit('do-not-update-orders')
    },
    handleUpdateOrders() {
      this.$emit('update-orders', this.selectedOrders)
    },
    toggleOrder(orderId) {
      const index = this.selectedOrders.indexOf(orderId)
      if (index > -1) {
        this.selectedOrders.splice(index, 1)
      } else {
        this.selectedOrders.push(orderId)
      }
    },
    onSearchOrders(searchString) {
      this.searchText = searchString
      const searchRegex = new RegExp(searchString, 'i')
      this.orderList = this.orderData.filter(
        order => searchRegex.test(order?.orderSeqId) || searchRegex.test(order?.retailerName),
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-currency-update {
  &__message {
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    text-align: center;
    line-height: 1.6;
    margin: 0;
  }

  &__search-row {
    margin-bottom: var(--v2-space-4);
  }

  &__search-input {
    width: 100%;
  }

  &__orders-header {
    display: flex;
    gap: var(--v2-space-4);
    padding: var(--v2-space-2) var(--v2-space-3);
    padding-left: calc(var(--v2-space-3) + 16px + var(--v2-space-3));
    background: var(--v2-muted);
    border-radius: var(--v2-radius-sm);
    font-family: var(--v2-font-sans);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: var(--v2-space-2);
  }

  &__orders-col {
    min-width: 0;
    word-break: break-word;

    &--id {
      flex: 2;
      font-family: var(--v2-font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
      font-size: 0.8125rem;
      color: var(--v2-foreground);
    }

    &--retailer {
      flex: 1;
      padding-left: var(--v2-space-3);
      border-left: 1px solid var(--v2-border);
      color: var(--v2-muted-foreground);
    }
  }

  &__orders-list {
    max-height: 300px;
    overflow-y: auto;
  }

  &__order-row {
    display: flex;
    align-items: flex-start;
    gap: var(--v2-space-4);
    padding: var(--v2-space-2) var(--v2-space-3);
    border-bottom: 1px solid var(--v2-border);
    cursor: pointer;
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    transition: background 0.1s ease;

    &:hover {
      background: var(--v2-muted);
    }
  }

  &__order-checkbox {
    margin-right: var(--v2-space-3);
    margin-top: 2px;
    accent-color: var(--v2-primary);
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    cursor: pointer;
  }
}
</style>
